import { apiClient } from './client';

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'suspended' | 'trial';
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
  // Computed fields
  user_count?: number;
  document_count?: number;
}

export interface CreateTenantInput {
  name: string;
  slug?: string;
  plan?: 'starter' | 'professional' | 'enterprise';
  status?: 'active' | 'suspended' | 'trial';
  settings?: Record<string, any>;
  // Admin user fields
  adminEmail?: string;
  adminPassword?: string;
  adminFirstName?: string;
  adminLastName?: string;
  sendWelcomeEmail?: boolean;
}

export interface UpdateTenantInput {
  name?: string;
  slug?: string;
  plan?: 'starter' | 'professional' | 'enterprise';
  status?: 'active' | 'suspended' | 'trial';
  settings?: Record<string, any>;
}

export interface TenantsResponse {
  success: boolean;
  data: Tenant[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AdminUserInfo {
  id: string;
  email: string;
  cognitoUserId: string;
  temporaryPassword?: string;
}

export interface TenantWithAdmin extends Tenant {
  adminUser?: AdminUserInfo;
}

export interface TenantResponse {
  success: boolean;
  data: TenantWithAdmin;
}

export interface TenantUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'super_admin' | 'tenant_admin' | 'manager' | 'user';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  lastLoginAt?: string;
  createdAt: string;
}

export const tenantsApi = {
  // List all tenants (super admin only)
  list: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    plan?: string;
    search?: string;
  }): Promise<TenantsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.plan) searchParams.set('plan', params.plan);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const response = await apiClient.get(`/tenants${query ? `?${query}` : ''}`);
    return response.data;
  },

  // Get single tenant
  get: async (id: string): Promise<TenantResponse> => {
    const response = await apiClient.get(`/tenants/${id}`);
    return response.data;
  },

  // Create tenant
  create: async (data: CreateTenantInput): Promise<TenantResponse> => {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },

  // Update tenant
  update: async (id: string, data: UpdateTenantInput): Promise<TenantResponse> => {
    const response = await apiClient.put(`/tenants/${id}`, data);
    return response.data;
  },

  // Delete tenant
  delete: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/tenants/${id}`);
    return response.data;
  },

  // Get tenant stats
  getStats: async (id: string): Promise<{
    success: boolean;
    data: {
      userCount: number;
      documentCount: number;
      departmentCount: number;
      storageUsed: number;
    };
  }> => {
    const response = await apiClient.get(`/tenants/${id}/stats`);
    return response.data;
  },

  // Get users for a specific tenant (super admin only)
  getUsers: async (tenantId: string, params?: {
    limit?: number;
    offset?: number;
    search?: string;
    status?: string;
    role?: string;
  }): Promise<{
    success: boolean;
    data: TenantUser[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.role) searchParams.set('role', params.role);

    const query = searchParams.toString();
    const response = await apiClient.get(`/tenants/${tenantId}/users${query ? `?${query}` : ''}`);
    return response.data;
  },

  // Reset user password (super admin only)
  resetUserPassword: async (tenantId: string, userId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/tenants/${tenantId}/users/${userId}/reset-password`, {});
    return response.data;
  },
};
